import React from 'react';
import type { FC } from 'react';
import { graphql } from 'react-relay';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	STAR_BUTTON_SHORTCUT,
	UNIVERSAL_STAR_BUTTON_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import type { ShortcutEvent } from '@confluence/shortcuts/entry-points/ShortcutListener.ts';
import { PageStar } from '@confluence/action-buttons/entry-points/pageStar.ts';

import type {
	FavouriteContentShortcutListenerQuery$data,
	FavouriteContentShortcutListenerQuery$variables,
} from './__generated__/FavouriteContentShortcutListenerQuery.graphql.ts';

const getStarShortcut = (contentType?: string, contentMode?: string) => {
	if (contentType === 'page' || contentType === 'blogpost') {
		if (contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft') {
			return {
				deprecatedAccelerator: undefined,
				accelerator: UNIVERSAL_STAR_BUTTON_SHORTCUT,
			};
		} else if (contentMode === 'view') {
			return {
				deprecatedAccelerator: STAR_BUTTON_SHORTCUT,
				accelerator: UNIVERSAL_STAR_BUTTON_SHORTCUT,
			};
		} else {
			return {
				deprecatedAccelerator: undefined,
				accelerator: undefined,
			};
		}
	} else {
		// for all other content types, use both keyboard shortcuts
		return {
			deprecatedAccelerator: STAR_BUTTON_SHORTCUT,
			accelerator: UNIVERSAL_STAR_BUTTON_SHORTCUT,
		};
	}
};

export const getStarShortcutString = (contentType?: string, contentMode?: string) => {
	if (contentType === 'page' || contentType === 'blogpost') {
		if (
			contentMode === 'live' ||
			contentMode === 'edit' ||
			contentMode === 'draft' ||
			contentMode === 'view'
		) {
			return UNIVERSAL_STAR_BUTTON_SHORTCUT;
		} else {
			return null;
		}
	} else {
		return UNIVERSAL_STAR_BUTTON_SHORTCUT;
	}
};

type FavouriteContentShortcutListenerType = {
	contentId: string;
	contentTitle: string;
	contentMode?: string;
	contentType?: string;
	source?: string;
};

export const FavouriteContentShortcutListener: FC<FavouriteContentShortcutListenerType> = ({
	contentId,
	contentTitle,
	contentMode,
	contentType,
	source = 'FavouriteMoreActionsMenuItem',
}) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { accelerator, deprecatedAccelerator } = getStarShortcut(contentType, contentMode);

	const FavouriteContentShortcutListenerQuery = graphql`
		query FavouriteContentShortcutListenerQuery($contentId: ID!) {
			singleContent(id: $contentId) {
				id
				type
				metadata {
					currentuser {
						favourited {
							isFavourite
						}
					}
				}
			}
			contentOperations: singleContent(id: $contentId) {
				id
				operations {
					operation
					targetType
				}
			}
		}
	`;

	const { data, loading, error } = useQueryInterop<
		FavouriteContentShortcutListenerQuery$data,
		FavouriteContentShortcutListenerQuery$variables
	>(FavouriteContentShortcutListenerQuery, {
		variables: {
			contentId,
		},
		fetchPolicy: 'cache-first',
	});

	if (loading || error || !accelerator) {
		return null;
	}

	const isStarred = Boolean(data?.singleContent?.metadata.currentuser?.favourited?.isFavourite);

	const handleShortcut = (toggleStar) => (e: KeyboardEvent | ShortcutEvent) => {
		toggleStar(e);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'pageStar',
				attributes: {
					triggeredByKeyboardShortcut: true,
					action: isStarred ? 'unstar' : 'star',
					isInEditor: contentMode === 'live' || contentMode === 'edit' || contentMode === 'draft',
				},
				source,
			},
		}).fire();
	};

	return (
		<PageStar
			contentId={contentId}
			isStarred={isStarred}
			contentTitle={contentTitle}
			shouldShowFlags
		>
			{({ toggle: toggleStar }) => (
				<GeneralShortcutListener
					deprecatedAccelerator={deprecatedAccelerator}
					accelerator={accelerator}
					listener={handleShortcut(toggleStar)}
				/>
			)}
		</PageStar>
	);
};
